import { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from '@sunrun/experience-ui-components';
import { DashboardWidgetContainer } from 'components/atoms/DashboardWidgetContainer';
import {
  DashboardLayoutItem,
  BLOCK_ROW_HEIGHT_PX,
  BLOCK_ROW_GAP_PX,
} from 'components/molecules/DashboardLayout';
import { ListViewRowMobile } from 'components/molecules/ListViewRowMobile';
import {
  ListViewEqualHeader,
  ListViewEqualRow,
} from 'components/molecules/ListViewTable';
import {
  REFERRAL_LIST_VIEW_COLUMNS,
  getReferralListViewMobileCreatedDate,
  getReferralListViewMobileName,
} from 'constants/referralListViewColumns';
import { getReferralMobileStatusPill } from 'helpers/referralHelpers';
import {
  sortReferralProspects,
  type ReferralSortField,
} from 'helpers/referralHelpers';
import { useClickOutside } from 'hooks/useClickOutside';
import { useListViewFloaterMenu } from 'hooks/useListViewFloaterMenu';
import { useListViewSortState } from 'hooks/useListViewSortState';
import { REFERRAL_CENTER__OPENED_ROW_ACTIONS_MENU } from 'constants/mixpanelEvents';
import { useReferralProspects } from 'hooks/useReferralProspects';
import { Mixpanel } from 'providers/Mixpanel';
import { Prospect } from 'types/prospect';

const COLLAPSED_BLOCK_ROWS = { mobile: 6, tablet: 6, desktop: 4 };
const HEADER_HEIGHT_PX = 52;
const ROW_HEIGHT_PX = 72;
const MOBILE_ROW_HEIGHT_PX = 108;
const MAX_EXPANDED_ROWS = 20;
const MIN_ROWS_TO_EXPAND = 3;
const EXPAND_ICON_SIZE = 24;

type ReferralCenterWidgetProps = {
  referrals: Prospect[];
  isLoading: boolean;
  error: unknown;
  isExpanded?: boolean;
};

const ReferralCenterWidget = ({
  referrals,
  isLoading,
  error,
  isExpanded = false,
}: ReferralCenterWidgetProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const {
    sortField,
    sortDirection,
    onSort: onSortClick,
  } = useListViewSortState<ReferralSortField>('created', 'DESC');
  const {
    closeFloaterMenu,
    isFloaterMenuOpen,
    onDotsClick,
    onRowBodyClick,
    openRowKey,
  } = useListViewFloaterMenu();

  const trackReferralRowActionsMenuOpened = useCallback(
    (prospect: Prospect) => {
      Mixpanel.track(REFERRAL_CENTER__OPENED_ROW_ACTIONS_MENU, {
        prospect_id: prospect.prospectId,
        lead_id: prospect.leadId,
        opportunity_id: prospect.opportunityId,
      });
    },
    [],
  );

  const handleReferralDotsClick = useCallback(
    (prospect: Prospect) => {
      const rowKey = prospect.prospectId;
      const isOpening = !isFloaterMenuOpen(rowKey);
      onDotsClick(rowKey);
      if (isOpening) {
        trackReferralRowActionsMenuOpened(prospect);
      }
    },
    [isFloaterMenuOpen, onDotsClick, trackReferralRowActionsMenuOpened],
  );

  const handleReferralRowBodyClick = useCallback(
    (prospect: Prospect) => {
      const rowKey = prospect.prospectId;
      const isOpening = openRowKey === null;
      onRowBodyClick(rowKey);
      if (isOpening) {
        trackReferralRowActionsMenuOpened(prospect);
      }
    },
    [openRowKey, onRowBodyClick, trackReferralRowActionsMenuOpened],
  );

  const sortedReferrals = useMemo(
    () => sortReferralProspects(referrals, sortField, sortDirection),
    [referrals, sortField, sortDirection],
  );

  const showError = !isLoading && !!error && referrals.length === 0;

  if (isLoading) {
    return (
      <LoadingContainer>
        {[1, 2, 3].map((i) => (
          <StyledSkeleton key={i} />
        ))}
      </LoadingContainer>
    );
  }

  if (showError) {
    return (
      <StateContainer>
        <SvgIcon
          name={SvgNames.ErrorOutline}
          width={28}
          height={28}
          color={tokens.TINTS_ERROR_40}
        />
        <Typography size="small" align="center">
          Couldn&apos;t load referrals. Please try again later.
        </Typography>
      </StateContainer>
    );
  }

  const isEmpty = referrals.length === 0;

  return (
    <TableContainer $isExpanded={isExpanded}>
      {!isMobile && (
        <ListViewEqualHeader
          columns={REFERRAL_LIST_VIEW_COLUMNS}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSortClick}
        />
      )}

      <ScrollableBody $scrollable={!isEmpty}>
        {isEmpty ? (
          <EmptyReferralsState>
            <Typography
              color={tokens.TINTS_OFF_WHITE_20}
              size="default"
              align="center">
              You currently don&apos;t have any referrals, ask every customer to
              refer one friend
            </Typography>
          </EmptyReferralsState>
        ) : (
          sortedReferrals.map((prospect) => (
            <ReferralListViewRow
              key={prospect.prospectId}
              prospect={prospect}
              isMobile={isMobile}
              isFloaterMenuOpen={isFloaterMenuOpen(prospect.prospectId)}
              onCloseFloaterMenu={closeFloaterMenu}
              onDotsClick={() => handleReferralDotsClick(prospect)}
              onRowBodyClick={() => handleReferralRowBodyClick(prospect)}
            />
          ))
        )}
      </ScrollableBody>
    </TableContainer>
  );
};

type ReferralListViewRowProps = {
  prospect: Prospect;
  isMobile: boolean;
  isFloaterMenuOpen: boolean;
  onCloseFloaterMenu: () => void;
  onDotsClick: () => void;
  onRowBodyClick: () => void;
};

const ReferralListViewRow = ({
  prospect,
  isMobile,
  isFloaterMenuOpen,
  onCloseFloaterMenu,
  onDotsClick,
  onRowBodyClick,
}: ReferralListViewRowProps) => {
  const name = getReferralListViewMobileName(prospect);
  const floaterRef = useClickOutside(onCloseFloaterMenu, isFloaterMenuOpen);

  if (isMobile) {
    return (
      <ListViewRowMobile
        variant="referral"
        prospect={prospect}
        isEven={false}
        statusPill={getReferralMobileStatusPill(prospect)}
        createdDate={getReferralListViewMobileCreatedDate(prospect)}
        name={name}
        referredBy={prospect.referredBy}
        isFloaterMenuOpen={isFloaterMenuOpen}
        onDotsClick={onDotsClick}
        floaterRef={floaterRef}
      />
    );
  }

  return (
    <ListViewEqualRow
      prospect={prospect}
      columns={REFERRAL_LIST_VIEW_COLUMNS}
      isEven={false}
      filledBackground
      multiline
      isFloaterMenuOpen={isFloaterMenuOpen}
      floaterRef={floaterRef}
      onDotsClick={onDotsClick}
      onRowBodyClick={onRowBodyClick}
      actionsAriaLabel={`Actions for ${name}`}
      floaterPlacement="start"
    />
  );
};

function calculateExpandedBlockRows(
  referralsCount: number,
  isMobile: boolean,
): number {
  const headerRowHeightPx = isMobile ? 0 : ROW_HEIGHT_PX;
  const rowHeightPx = isMobile ? MOBILE_ROW_HEIGHT_PX : ROW_HEIGHT_PX;
  const contentHeightPx =
    HEADER_HEIGHT_PX + headerRowHeightPx + referralsCount * rowHeightPx;

  const blockRows = Math.ceil(
    (contentHeightPx + BLOCK_ROW_GAP_PX) /
      (BLOCK_ROW_HEIGHT_PX + BLOCK_ROW_GAP_PX),
  );

  const maxContentHeightPx = HEADER_HEIGHT_PX + MAX_EXPANDED_ROWS * rowHeightPx;
  const maxExpandedRows = Math.ceil(
    (maxContentHeightPx + BLOCK_ROW_GAP_PX) /
      (BLOCK_ROW_HEIGHT_PX + BLOCK_ROW_GAP_PX),
  );

  return Math.min(blockRows, maxExpandedRows);
}

function getReferralCenterBlockRows(
  referralsCount: number,
  isMobile: boolean,
  isExpanded: boolean,
  isDesktop = !isMobile,
) {
  if (isExpanded) {
    return calculateExpandedBlockRows(referralsCount, isMobile);
  }

  if (isDesktop) {
    return COLLAPSED_BLOCK_ROWS.desktop;
  }

  return isMobile ? COLLAPSED_BLOCK_ROWS.mobile : COLLAPSED_BLOCK_ROWS.tablet;
}

type ReferralCenterCardProps = {
  referrals: Prospect[];
  isLoading: boolean;
  error: unknown;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

const ReferralCenterCard = ({
  referrals,
  isLoading,
  error,
  isExpanded,
  onToggleExpand,
}: ReferralCenterCardProps) => {
  const canExpand = referrals.length > MIN_ROWS_TO_EXPAND;

  return (
    <DashboardWidgetContainer
      header="Referral Center"
      preserveHeaderCase
      headerAction={
        canExpand
          ? {
              icon: isExpanded ? SvgNames.Shrink : SvgNames.ArrowsOutSimple,
              iconSize: EXPAND_ICON_SIZE,
              onClick: onToggleExpand,
              ariaLabel: isExpanded ? 'Collapse' : 'Expand',
            }
          : undefined
      }>
      <ReferralCenterWidget
        referrals={referrals}
        isLoading={isLoading}
        error={error}
        isExpanded={isExpanded}
      />
    </DashboardWidgetContainer>
  );
};

type ReferralCenterLayoutItemProps = {
  isMobile: boolean;
};

const ReferralCenterLayoutItem = ({
  isMobile,
}: ReferralCenterLayoutItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isTablet = useSingletonTabletMediaQuery();
  const isDesktop = !isMobile && !isTablet;
  const { referrals, isLoading, error } = useReferralProspects();

  const blockRows = getReferralCenterBlockRows(
    referrals.length,
    isMobile,
    isExpanded,
    isDesktop,
  );

  return (
    <DashboardLayoutItem blockColumns={isMobile ? 1 : 2} blockRows={blockRows}>
      <ReferralCenterCard
        referrals={referrals}
        isLoading={isLoading}
        error={error ?? undefined}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((value) => !value)}
      />
    </DashboardLayoutItem>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  height: 40px;
  min-height: 40px;
`;

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  text-align: center;
`;

const TableContainer = styled.div<{ $isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  flex: 1;

  ${({ $isExpanded }) =>
    $isExpanded ? `overflow: visible;` : `overflow: hidden;`}
`;

const ScrollableBody = styled.div<{ $scrollable?: boolean }>`
  flex: 1;
  min-height: 0;
  margin-bottom: 6px;
  overflow-x: hidden;
  overflow-y: ${({ $scrollable }) => ($scrollable ? 'auto' : 'hidden')};
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmptyReferralsState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 12px;
  text-align: center;
  box-sizing: border-box;
`;

export {
  ReferralCenterWidget,
  ReferralCenterCard,
  ReferralCenterLayoutItem,
  calculateExpandedBlockRows,
  getReferralCenterBlockRows,
};
